import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Check } from "lucide-react";
import { GOAL_LABELS, GOAL_TYPES, type GoalType } from "@omina/core";

import { useSetActiveGoal } from "@/lib/queries";
import { cn } from "@/lib/utils";

/**
 * Choose your path — step 4.
 *
 * One goal, not several, and that is the whole design of the screen. Every
 * ranked surface downstream sorts by a single track: `goalFit` is
 * `{cust, auth, fund}` and the radar, the map and the classifier all read one
 * of them. A founder who picks three has picked none, and the product would
 * have to invent a blend nobody asked for to show her anything.
 *
 * It writes `org_settings.active_goal`, which is the pointer the rest of the
 * product already follows — the sidebar, the radar and the map all read it.
 * So this is not a new concept, it is the existing one asked at the moment it
 * first matters instead of buried in Settings.
 *
 * "Learn" is a real answer and deliberately unranked. The other three score
 * against the curated base; learning has no buyer ladder to sort by, so it
 * returns rooms in date order and says so rather than inventing a fourth score
 * to sit next to three measured ones.
 */
export const Route = createFileRoute("/path")({
  component: ChoosePath,
});

/** One line each, in her language rather than the product's. */
const WHY: Record<GoalType, string> = {
  fund: "Rooms where investors actually are.",
  cust: "Rooms where the people who buy are.",
  auth: "Rooms where being on the stage compounds.",
  learn: "Rooms that teach, in date order.",
};

function ChoosePath() {
  const setGoal = useSetActiveGoal();
  const navigate = useNavigate();

  /**
   * Nothing preselected, and the current goal is not read at all.
   *
   * `org_settings.active_goal` always holds a value — it defaults to `cust`
   * for every new org — so seeding from it would light a card she never chose
   * and turn a decision into something to disagree with. Same reason the
   * interests chips start empty.
   *
   * Switching an existing goal is therefore silent here. That is fine on this
   * screen, which is step 4 of onboarding and only ever seen once; a founder
   * changing tracks later does it from Settings, where the consequences have
   * room to be spelled out.
   */
  const [choice, setChoice] = useState<GoalType | null>(null);

  return (
    <div className="pathwrap">
      <div className="eyebrow">Choose your path</div>
      <h1 className="tk tk-want">What is your biggest goal for the next 90 days?</h1>
      <p className="lede pathlede">
        Omina ranks every room against this. It is not a commitment — change it
        whenever the work changes.
      </p>

      <ul className="pathlist">
        {GOAL_TYPES.map((g) => {
          const on = choice === g;
          return (
            <li key={g}>
              <button
                type="button"
                className={cn("pathcard", on && "on")}
                aria-pressed={on}
                onClick={() => setChoice(g)}
              >
                <span className="pathtext">
                  <b>{GOAL_LABELS[g].title}</b>
                  <span>{WHY[g]}</span>
                </span>
                {/* The tick only exists on the chosen one. A permanently
                    visible empty circle on all four reads as a checklist she
                    is meant to complete. */}
                {on && (
                  <span className="pathtick" aria-hidden>
                    <Check size={14} />
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>

      <div className="pathsave">
        <button
          type="button"
          className={cn("btn", setGoal.isPending && "loading")}
          disabled={!choice || setGoal.isPending}
          onClick={() =>
            choice &&
            setGoal.mutate(choice, {
              /**
               * The feed, which is the end of onboarding.
               *
               * It used to be `/matches` — three rooms with two blurred behind
               * an upgrade nudge. The feed now does that job better, because
               * it is the same screen she will live in afterwards: two real
               * rooms, and the rest behind the blueprint conversation rather
               * than behind a price. What she gives to unlock it is the thing
               * that makes the ranking hers.
               *
               * Step 5 — the "we are looking" animation — still does not
               * exist. When it lands it goes between these two and nothing
               * else changes.
               */
              onSuccess: () => void navigate({ to: "/feed" }),
            })
          }
        >
          <span className="sp" aria-hidden />
          {setGoal.isPending ? "Setting up…" : "Find my rooms"}
        </button>
      </div>

      {setGoal.isError && <div className="danger">{setGoal.error.message}</div>}
    </div>
  );
}
